import { Box, ButtonBase, Typography } from "@mui/material";

export interface StudentCellProps {
  firstName: string;
  lastName: string;
  /** Muted second line (table, age, source). */
  meta?: string;
  /** Makes the cell a button that opens the student record. */
  onClick?: () => void;
  /** Avatar diameter in px (design: 32 in grids, 30 in rosters). */
  size?: number;
}

export const initialsOf = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

/** Roster row lead: initials in a blue-soft circle, the name and a muted meta line (design grid rows). */
export const StudentCell = ({
  firstName,
  lastName,
  meta,
  onClick,
  size = 32,
}: StudentCellProps) => {
  const name = `${lastName}, ${firstName}`;
  const body = (
    <>
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          flex: "none",
          borderRadius: "50%",
          bgcolor: "primary.light",
          color: "primaryInk",
          display: "grid",
          placeItems: "center",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {initialsOf(firstName, lastName)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {name}
        </Typography>
        {meta ? (
          <Typography
            component="span"
            sx={{
              display: "block",
              fontSize: 11.5,
              lineHeight: 1.2,
              color: "text.secondary",
            }}
          >
            {meta}
          </Typography>
        ) : null}
      </Box>
    </>
  );
  const layout = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    textAlign: "left" as const,
  };
  if (onClick) {
    return (
      <ButtonBase
        onClick={onClick}
        sx={(theme) => ({
          ...layout,
          borderRadius: `${theme.shape.borderRadius}px`,
          px: 0.5,
          py: 0.25,
          mx: -0.5,
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.focusRing}`,
            outlineOffset: 2,
          },
        })}
      >
        {body}
      </ButtonBase>
    );
  }
  return <Box sx={layout}>{body}</Box>;
};

export default StudentCell;
